import { useQuery } from "@apollo/client";
import { Delete, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { ReactElement, useCallback, useMemo, useState } from "react";

import {
  EditCampaignTagGroupsMutation,
  EditCampaignTagGroupsMutationVariables,
  EditCampaignTagsMutation,
  EditCampaignTagsMutationVariables,
  ListTagsAndGroupsWithIntegrationsQuery,
  ListTagsAndGroupsWithIntegrationsQueryVariables,
  NgpVanDatabaseModeGqlType,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import Table, { TableColumns } from "~frontend/components/Table";
import TagChip from "~frontend/components/TagChip";
import {
  EDIT_CAMPAIGN_TAG_GROUPS,
  EDIT_CAMPAIGN_TAGS,
} from "~frontend/graphql/campaigns";
import {
  LIST_TAGS_AND_GROUPS_WITH_INTEGRATIONS,
  TagOrGroupDetail,
} from "~frontend/graphql/tags";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import {
  nestedTagTableGetChildRows,
  nestedTagTableRenderName,
} from "../../tags/TagTable";
import { renderActionsForIntegration } from "../../tags/tagTableRenderers";
import { StepInstructions } from "../StepInstructions";
import SaveButtons from "../campaignEditorUtils/SaveButtons";
import { CampaignForEditor } from "../types";
import GlobalTags from "./GlobalTags";

export default function Tags({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignForEditor;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const [globalTagsExpanded, setGlobalTagsExpanded] = useState(
    campaign.tags.length === 0
  );

  const listTagsResult = useQuery<
    ListTagsAndGroupsWithIntegrationsQuery,
    ListTagsAndGroupsWithIntegrationsQueryVariables
  >(LIST_TAGS_AND_GROUPS_WITH_INTEGRATIONS, {
    variables: {
      subaccountId: subaccount.id,
    },
    fetchPolicy: "cache-and-network",
  });

  const [editCampaignTags] = useMutation<
    EditCampaignTagsMutation,
    EditCampaignTagsMutationVariables
  >(EDIT_CAMPAIGN_TAGS, { onErrorSnackbarMessage: "Error updating tags." });

  const [editCampaignTagGroups] = useMutation<
    EditCampaignTagGroupsMutation,
    EditCampaignTagGroupsMutationVariables
  >(EDIT_CAMPAIGN_TAG_GROUPS, {
    onErrorSnackbarMessage: "Error updating tags.",
  });

  const handleAddTagsAndGroups = useCallback(
    async (tagIds: string[], tagGroupIds: string[]) => {
      const [{ errors: tagErrors }, { errors: tagGroupErrors }] =
        await Promise.all([
          editCampaignTags({
            variables: {
              input: {
                campaignId: campaign.id,
                tagIds: [...campaign.tags.map(tag => tag.id), ...tagIds],
              },
            },
          }),
          editCampaignTagGroups({
            variables: {
              input: {
                campaignId: campaign.id,
                tagGroupIds: [
                  ...campaign.tagGroups.map(tagGroup => tagGroup.id),
                  ...tagGroupIds,
                ],
              },
            },
          }),
        ]);

      if (!tagErrors && !tagGroupErrors) {
        setGlobalTagsExpanded(false);
      } else {
        throw new Error("Error adding tags");
      }
    },
    [campaign, editCampaignTags, editCampaignTagGroups]
  );

  const handleRemoveTag = useCallback(
    async (tagId: string) => {
      await editCampaignTags({
        variables: {
          input: {
            campaignId: campaign.id,
            tagIds: campaign.tags.map(tag => tag.id).filter(id => id !== tagId),
          },
        },
      });
    },
    [campaign, editCampaignTags]
  );

  const handleRemoveTagGroup = useCallback(
    async (tagGroupId: string) => {
      await editCampaignTagGroups({
        variables: {
          input: {
            campaignId: campaign.id,
            tagGroupIds: campaign.tagGroups
              .map(tagGroup => tagGroup.id)
              .filter(id => id !== tagGroupId),
          },
        },
      });
    },
    [campaign, editCampaignTagGroups]
  );

  const handleRemoveAllTagsAndGroups = useCallback(async () => {
    await Promise.all([
      editCampaignTags({
        variables: {
          input: {
            campaignId: campaign.id,
            tagIds: [],
          },
        },
      }),
      editCampaignTagGroups({
        variables: {
          input: {
            campaignId: campaign.id,
            tagGroupIds: [],
          },
        },
      }),
    ]);
  }, [campaign, editCampaignTags, editCampaignTagGroups]);

  const tagIdsInCampaign = useMemo(
    () => new Set(campaign.tags.map(tag => tag.id)),
    [campaign.tags]
  );

  const tagGroupIdsInCampaign = useMemo(
    () => new Set(campaign.tagGroups.map(tagGroup => tagGroup.id)),
    [campaign.tagGroups]
  );

  const campaignTagsAndGroups = useMemo(
    () =>
      [
        ...(listTagsResult.data?.listTags || []).filter(tag =>
          tagIdsInCampaign.has(tag.id)
        ),
        ...(listTagsResult.data?.listTagGroups || []).filter(tagGroup =>
          tagGroupIdsInCampaign.has(tagGroup.id)
        ),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [listTagsResult.data, tagGroupIdsInCampaign, tagIdsInCampaign]
  );

  const responseScriptTagsInCampaign = useMemo(
    () =>
      new Set(
        _.chain(campaign.responseScriptGroups)
          .flatMap(group => group.memberships)
          .flatMap(groupMember => groupMember.script.tags)
          .map(t => t.id)
          .value()
      ),
    [campaign.responseScriptGroups]
  );

  const responseScriptTags = useMemo(
    () =>
      [
        ...(listTagsResult.data?.listTags ?? [])
          .filter(tag => responseScriptTagsInCampaign.has(tag.id))
          .map(t => ({
            ...t,
            displayName: t.name,
          })),
        ...(listTagsResult.data?.listTagGroups ?? []).flatMap(tagGroup =>
          tagGroup.tags
            .filter(tag => responseScriptTagsInCampaign.has(tag.id))
            .map(t => ({
              ...t,
              displayName: `${tagGroup.name} > ${t.name}`,
            }))
        ),
      ].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [listTagsResult.data, responseScriptTagsInCampaign]
  );

  let integrationTableColumns: TableColumns<TagOrGroupDetail> = [];
  if (campaign.integration && campaign.integrationSettings) {
    const integrationId = campaign.integration.id;
    const databaseMode = campaign.integrationSettings
      .databaseMode as NgpVanDatabaseModeGqlType;

    integrationTableColumns = [
      {
        key: "integration",
        title: "Integration Action",
        renderValue: tag =>
          tag.__typename === "Tag"
            ? renderActionsForIntegration(tag, integrationId, databaseMode)
            : null,
        align: "left",
      },
    ];
  }

  return (
    <>
      <FlexBox direction="column" gap={4}>
        <Box>
          <StepInstructions header="Tags">
            Choose which global tags are available for your texters to select
            manually. Any tags attached to response scripts in your campaign
            will also be available to select manually.
          </StepInstructions>
        </Box>

        <Accordion
          expanded={globalTagsExpanded}
          onChange={(_, expanded) => setGlobalTagsExpanded(expanded)}
          sx={{
            padding: 0.5,
            mb: `0 !important`,
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <strong>Global Tags</strong>
          </AccordionSummary>
          <AccordionDetails>
            {listTagsResult.loading ? (
              <Loading />
            ) : (
              <GlobalTags
                tags={listTagsResult.data!.listTags.filter(
                  tag => !tagIdsInCampaign.has(tag.id)
                )}
                tagGroups={listTagsResult.data!.listTagGroups.filter(
                  tagGroup => !tagGroupIdsInCampaign.has(tagGroup.id)
                )}
                onAddTagsAndGroups={handleAddTagsAndGroups}
                integrationTableColumns={integrationTableColumns}
              />
            )}
          </AccordionDetails>
        </Accordion>

        {(campaign.tags.length > 0 || campaign.tagGroups.length > 0) && (
          <Card>
            <CardHeader title="Manual Tags" />
            <CardContent>
              <Table
                size="small"
                columns={[
                  {
                    key: "name",
                    title: "Tag Name",
                    renderValue: nestedTagTableRenderName,
                  },
                  ...integrationTableColumns,
                ]}
                rowActions={[
                  {
                    id: "delete",
                    icon: <Delete />,
                    tooltipLabel: "Remove from campaign",
                    onClick: tagOrGroup =>
                      tagOrGroup.__typename === "TagGroup"
                        ? handleRemoveTagGroup(tagOrGroup.id)
                        : handleRemoveTag(tagOrGroup.id),
                  },
                ]}
                rowActionsLabel="Delete"
                actionSx={{ color: palette.darkGray, fontWeight: 600 }}
                rows={campaignTagsAndGroups ?? []}
                noFooter
                getChildRows={nestedTagTableGetChildRows}
              />
              <FlexBox
                justifyContent="flex-end"
                direction="row"
                sx={{ paddingTop: theme.spacing(2) }}
              >
                <Button
                  variant="contained"
                  color="accessory"
                  onClick={handleRemoveAllTagsAndGroups}
                >
                  <Typography variant="label3Caps">Delete All</Typography>
                </Button>
              </FlexBox>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader title="Response Script Tags" />
          <CardContent sx={{ pb: "0px !important" }}>
            <Typography variant="body2">
              These are tags that are linked to the Response Scripts you’ve made
              available in your campaign. Any tags from a campaign’s Response
              Scripts will also be available for texters to select manually. If
              you remove a Response Script from your campaign, its tags will
              also be removed from this list.
            </Typography>
            <Table
              columns={[
                {
                  key: "name",
                  title: "Tag Name",
                  renderValue: tag => <TagChip label={tag.displayName} />,
                },
                ...integrationTableColumns,
              ]}
              rows={responseScriptTags ?? []}
              noFooter
            />
          </CardContent>
        </Card>

        <SaveButtons
          variant="continueOnly"
          handleSave={() => goToNextSection()}
        />
      </FlexBox>
    </>
  );
}
